// src/components/admin/AmenitiesServicesAdmin.js

import React, { useEffect, useState } from "react";
import { ref, push, onValue, remove, update } from "firebase/database";
import { database } from "../../../config/firebaseConfig";
import { uploadFile } from "../../../config/uploadFile";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import ImageUploadField from "../../../utils/ImageUploadField";

export default function AmenitiesServicesAdmin() {
  const [services, setServices] = useState({});
  const [name, setName] = useState("");
  const [highlight, setHighlight] = useState(false);
  
  // Fields for Dark Icon (image)
  const [darkFile, setDarkFile] = useState(null);
  const [darkUrl, setDarkUrl] = useState("");
  const [isUploadingDark, setIsUploadingDark] = useState(false);

  // Fields for Light Icon (imageLight)
  const [lightFile, setLightFile] = useState(null);
  const [lightUrl, setLightUrl] = useState("");
  const [isUploadingLight, setIsUploadingLight] = useState(false);

  const [editId, setEditId] = useState(null);

  useEffect(() => {
    const servicesRef = ref(database, "home/amenitiesServices");
    const unsubscribe = onValue(servicesRef, (snapshot) => {
      const data = snapshot.val();
      console.log("Fetched services:", data);
      setServices(data || {});
    });
    return () => unsubscribe();
  }, []);

  const handleUpload = async (file, setUrl, setUploading) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setUrl(url);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveService = async () => {
    console.log("Attempting to save service:", { name, editId, darkUrl, lightUrl });

    if (!name.trim()) {
      toast.error("Please provide a name.");
      return;
    }

    let finalDarkUrl = darkUrl;
    let finalLightUrl = lightUrl;

    // Automatic upload if files are selected but not yet uploaded
    try {
      if (darkFile) {
        toast.loading("Uploading dark icon...", { id: "upload-dark" });
        finalDarkUrl = await uploadFile(darkFile);
        setDarkUrl(finalDarkUrl);
        setDarkFile(null);
        toast.success("Dark icon uploaded", { id: "upload-dark" });
      }

      if (lightFile) {
        toast.loading("Uploading light icon...", { id: "upload-light" });
        finalLightUrl = await uploadFile(lightFile);
        setLightUrl(finalLightUrl);
        setLightFile(null);
        toast.success("Light icon uploaded", { id: "upload-light" });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("File upload failed: " + error.message);
      return;
    }

    if (!finalDarkUrl && !finalLightUrl) {
      toast.error("Please upload at least one icon.");
      return;
    }

    const serviceData = {
      name: name.trim(),
      highlight,
      image: finalDarkUrl || finalLightUrl,
      imageLight: finalLightUrl || finalDarkUrl,
    };

    console.log("Saving data to Firebase:", serviceData);

    try {
      if (editId) {
        await update(ref(database, `home/amenitiesServices/${editId}`), serviceData);
        console.log("Update successful");
        toast.success("Service updated successfully");
      } else {
        const servicesRef = ref(database, "home/amenitiesServices");
        await push(servicesRef, serviceData);
        console.log("Add successful");
        toast.success("Service added successfully");
      }

      // Reset
      setName("");
      setHighlight(false);
      setDarkUrl("");
      setLightUrl("");
      setDarkFile(null);
      setLightFile(null);
      setEditId(null);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Save failed: " + error.message);
    }
  };

  const handleEdit = (id, service) => {
    setName(service.name);
    setHighlight(service.highlight || false);
    setDarkUrl(service.image || "");
    setLightUrl(service.imageLight || "");
    setEditId(id);
    setDarkFile(null);
    setLightFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Service?",
      text: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      await remove(ref(database, `home/amenitiesServices/${id}`));
      toast.success("Service deleted successfully");
    }
  };

  return (
    <div className="p-6 mx-auto bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-6 text-[#14251F]">
        {editId ? "Edit Service" : "Add New Service"}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Service Name</label>
            <input
              className="border-2 rounded-lg p-3 w-full focus:border-[#14251F] outline-none"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Table Tennis"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-5 h-5 rounded"
              checked={highlight}
              onChange={(e) => setHighlight(e.target.checked)}
            />
            <span className="text-sm font-medium">Highlight Service</span>
          </label>

          <div className="pt-4 border-t">
            <button
              className={`w-full py-3 rounded-xl font-bold transition-all ${
                editId ? "bg-blue-600 hover:bg-blue-700" : "bg-[#14251F] hover:bg-[#1c352c]"
              } text-[#FFD1A0]`}
              onClick={handleSaveService}
            >
              {editId ? "Update Service" : "Add Service"}
            </button>
            {editId && (
              <button
                className="w-full mt-2 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold"
                onClick={() => {
                  setEditId(null);
                  setName("");
                  setHighlight(false);
                  setDarkUrl("");
                  setLightUrl("");
                }}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Dark Icon Upload */}
          <div className="p-4 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <ImageUploadField
              label="Dark Icon (for light background)"
              previewUrl={darkUrl}
              file={darkFile}
              onChange={(e) => setDarkFile(e.target.files[0])}
              onClear={() => { setDarkFile(null); setDarkUrl(""); }}
            />
            {darkFile && (
              <button
                className="mt-3 px-4 py-2 bg-[#14251F] text-white text-xs font-bold rounded-lg"
                onClick={() => handleUpload(darkFile, setDarkUrl, setIsUploadingDark)}
                disabled={isUploadingDark}
              >
                {isUploadingDark ? "Uploading..." : "Upload Dark Icon"}
              </button>
            )}
          </div>

          {/* Light Icon Upload */}
          <div className="p-4 bg-[#14251F] rounded-xl border-2 border-dashed border-[#FFD1A0]/20">
            <ImageUploadField
              label={<span className="text-[#FFD1A0]">Light Icon (for dark background)</span>}
              previewUrl={lightUrl}
              file={lightFile}
              onChange={(e) => setLightFile(e.target.files[0])}
              onClear={() => { setLightFile(null); setLightUrl(""); }}
            />
            {lightFile && (
              <button
                className="mt-3 px-4 py-2 bg-[#FFD1A0] text-[#14251F] text-xs font-bold rounded-lg"
                onClick={() => handleUpload(lightFile, setLightUrl, setIsUploadingLight)}
                disabled={isUploadingLight}
              >
                {isUploadingLight ? "Uploading..." : "Upload Light Icon"}
              </button>
            )}
          </div>
        </div>
      </div>

      <hr className="my-10" />

      <h3 className="text-2xl font-bold mb-6 text-[#14251F]">All Services</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(services).map(([id, srv]) => (
          <div key={id} className="border-2 rounded-2xl p-4 flex flex-col gap-4 bg-gray-50">
            <div className="flex items-center gap-4">
              <div className="flex gap-1">
                <div className="w-12 h-12 bg-white rounded-lg border flex items-center justify-center p-1">
                   <img src={srv.image} alt="dark" className="w-full h-full object-contain" />
                </div>
                <div className="w-12 h-12 bg-[#14251F] rounded-lg border flex items-center justify-center p-1">
                   <img src={srv.imageLight || srv.image} alt="light" className="w-full h-full object-contain" />
                </div>
              </div>
              <div className="flex-grow">
                <p className="font-bold text-gray-800">{srv.name}</p>
                {srv.highlight && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Featured</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="flex-grow py-2 bg-blue-100 text-blue-700 font-bold rounded-lg hover:bg-blue-200 transition-colors"
                onClick={() => handleEdit(id, srv)}
              >
                Edit
              </button>
              <button
                className="px-4 py-2 bg-red-100 text-red-700 font-bold rounded-lg hover:bg-red-200 transition-colors"
                onClick={() => handleDelete(id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
